import { useCallback, useMemo } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { TViewSettings } from '@valuecase/common'
import { DEFAULT_SPACE_SIDEBAR_SETTINGS } from '@valuecase/common/src/view-settings/view-settings'
import sellerApi from '@/api/SellerApi'
import { useNotifications } from '@valuecase/ui-components'

const SPACE_SIDEBAR_VIEW_QUERY_KEY = 'spaceSidebarView'

const getDefaultViewSettings = (): TViewSettings => {
  const now = new Date().toISOString()
  return {
    id: 'default',
    userId: 'default',
    viewType: 'SPACE_SIDEBAR',
    settings: DEFAULT_SPACE_SIDEBAR_SETTINGS,
    createdAt: now,
    updatedAt: now,
  } as TViewSettings
}

export const useSpaceSidebarView = () => {
  const queryClient = useQueryClient()
  const { success } = useNotifications()
  const {
    data: settings,
    isLoading,
    error,
  } = useQuery<TViewSettings>({
    queryKey: [SPACE_SIDEBAR_VIEW_QUERY_KEY],
    queryFn: async () => {
      try {
        const response = await sellerApi.get('/space-sidebar-view')
        return response.data
      } catch (error) {
        // If no settings exist, return default settings
        return getDefaultViewSettings()
      }
    },
  })

  const { mutateAsync: updateSettings } = useMutation({
    mutationFn: async (newSettings: Partial<TViewSettings['settings']>) => {
      const response = await sellerApi.put('/space-sidebar-view', {
        settings: newSettings,
        id: settings?.id,
      })
      return response.data
    },
    onSuccess: (data) => {
      queryClient.setQueryData([SPACE_SIDEBAR_VIEW_QUERY_KEY], data)
      success('View settings updated')
    },
  })

  const { mutateAsync: resetSettings } = useMutation({
    mutationFn: async () => {
      const response = await sellerApi.post('/space-sidebar-view/reset')
      return response.data
    },
    onSuccess: (data) => {
      queryClient.setQueryData([SPACE_SIDEBAR_VIEW_QUERY_KEY], data)
      success('View settings reset')
    },
  })

  const refetch = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: [SPACE_SIDEBAR_VIEW_QUERY_KEY] })
  }, [queryClient])

  const effectiveSettings = useMemo(() => {
    if (!settings) return getDefaultViewSettings()
    return settings
  }, [settings])

  return {
    settings: effectiveSettings,
    isLoading,
    error: error instanceof Error ? error : new Error('Failed to fetch view settings'),
    updateSettings,
    resetSettings,
    refetch,
  }
}
